<script lang="ts" setup>
const current = new Date()
const date = `${current.getFullYear()}`
const localeRoute = useLocaleRoute()
const { t } = useI18n()
const {
  public: { appStoreLink, googlePlayLink },
} = useRuntimeConfig()

const footerLinks = [
  {
    links: [
      {
        title: 'belong-app',
        to: localeRoute('/app'),
      },
      {
        title: 'belong-nft-ticketing',
        to: localeRoute('/nft-tickets'),
      },
      {
        title: 'belong-creator-pass',
        to: localeRoute('/creator-pass'),
      },
    ],
  },
  {
    links: [
      {
        title: 'belong-founder-pass',
        to: localeRoute('/founder-pass'),
      },
      {
        title: 'gated-content',
        to: localeRoute('/paid-content-nft'),
      },
      {
        title: 'community-management',
        to: localeRoute('/mighty-networks-alternative'),
      },
    ],
  },
  {
    links: [
      {
        title: 'blog',
        to: localeRoute('/blog'),
      },
      {
        title: 'hubs',
        to: localeRoute('/hubs'),
      },
      {
        title: 'events',
        to: localeRoute('/events'),
      },
    ],
  },
  {
    links: [
      {
        title: 'service-status',
        to: localeRoute('/service-status'),
      },
      {
        title: 'api-docs',
        to: localeRoute('/api-docs'),
      },
      {
        title: 'tutorials',
        to: localeRoute('/how-to'),
      },
    ],
  },
]

const downloadLinks = [
  {
    title: 'App Store',
    link: appStoreLink,
    icon: 'mdi:apple',
  },
  {
    title: 'Google Play',
    link: googlePlayLink,
    icon: 'mdi:google-play',
  },
]

const externalLinks = [
  {
    to: 'https://moralis.io/',
    ariaLabel: 'Moralis',
    icon: 'belong-moralis',
    class: 'md:w-23 md:h-4 md:w-32 md:h-5.5',
  },
  {
    to: 'https://audits.hacken.io/belong-net/',
    ariaLabel: 'Hacken',
    icon: 'belong-proofed',
    class: 'opacity-40 flex w-19 h-6.5 md:w-30.5 md:h-10.5',
  },
  {
    to: 'https://magic.store/app/belong',
    ariaLabel: 'Magic',
    imgSrc: '/images/home/validated-by-web.png',
  },
]
</script>

<template>
  <footer>
    <div class="flex flex-col items-center">
      <div
        class="pb-8 px-5 pt-16 md:w-full md:max-w-237.5 lg:px-0 xl:max-w-316.5 xl:py-27 xl:px-5 xl:w-full min-[1300px]:!px-0"
      >
        <div
          class="flex flex-col justify-center gap-5 md:gap-16 min-[1300px]:gap-0 xl:flex-row-reverse xl:justify-between"
        >
          <div
            class="md:flex md:flex-row md:gap-10 md:justify-between xl:flex-col xl:mx-0"
          >
            <div class="flex max-w-97 w-full mx-auto md:mx-0">
              <div
                class="rounded-[1.25rem] flex flex-row justify-between items-end px-4 pt-4 gap-4.5 min-[26.75rem]:gap-8.5 w-full"
                style="
                  background: linear-gradient(
                    180.11deg,
                    #100d0d 0.09%,
                    #7540ee 175.59%
                  );
                "
              >
                <div class="flex flex-col gap-4 pb-7.5">
                  <p class="text-[#ABABAB] text-[1.25rem]">
                    {{ t('download-belong') }}
                  </p>
                  <div>
                    <div
                      class="flex flex-row gap-3 min-[26.25rem]:gap-9.5 md:gap-6.5"
                    >
                      <NuxtLink
                        v-for="item in downloadLinks"
                        :to="item.link"
                        class="flex flex-row items-center gap-2 text-[#ABABAB]"
                        target="_blank"
                        :aria-label="item.title"
                      >
                        <Icon :name="item.icon" class="w-5 h-5" />

                        <p
                          class="uppercase font-medium tracking-wide text-xs min-[26.5625rem]:whitespace-pre"
                        >
                          {{ item.title }}
                        </p>
                      </NuxtLink>
                    </div>
                  </div>
                </div>

                <NuxtImg
                  loading="lazy"
                  format="webp"
                  src="/images/home/phone-footer.png"
                  class="max-w-16.5 w-16.5 h-24"
                  alt=""
                />
              </div>
            </div>

            <div
              class="hidden md:flex md:flex-row md:justify-end md:gap-6 md:items-center"
            >
              <NuxtLink
                v-for="item in externalLinks"
                class="flex"
                :to="item.to"
                target="_blank"
                :aria-label="item.ariaLabel"
              >
                <Icon v-if="item.icon" :name="item.icon" :class="item.class" />
                <NuxtImg
                  v-else
                  :src="item.imgSrc"
                  class="opacity-40 w-9 h-9"
                  alt=""
                />
              </NuxtLink>
            </div>
          </div>

          <div
            class="links flex flex-col text-center justify-center gap-5 md:flex-row md:text-left md:justify-between xl:gap-21 xl:flex-row xl:text-left"
          >
            <div v-for="link in footerLinks" class="flex flex-col gap-5">
              <NuxtLink
                v-for="item in link.links"
                :to="item.to"
                class="whitespace-pre-line !text-[#A3A3A3]"
              >
                {{ t(item.title) }}
              </NuxtLink>
            </div>
          </div>
        </div>

        <div class="my-5 md:mt-9.5 lg:mt-14">
          <img
            alt=""
            src="/assets/images/token/line.svg"
            class="h-0.25 w-full"
          />
        </div>
        <div
          class="flex flex-col md:flex-row items-center gap-5 xl:flex-row justify-between"
        >
          <div class="flex flex-row gap-5 md:order-1 items-center">
            <NuxtLink
              to="https://discord.gg/QzwrUXXWCA"
              target="_blank"
              class="flex items-center justify-center opacity-40"
              aria-label="Discord"
            >
              <Icon name="belong:discord" class="w-6 h-4.5 object-cover" />
            </NuxtLink>

            <NuxtLink
              to="https://twitter.com/belongnet"
              target="_blank"
              class="opacity-40 flex"
              aria-label="Twitter"
            >
              <Icon name="ri:twitter-x-fill" class="w-6 h-6 object-cover" />
            </NuxtLink>
          </div>

          <div
            class="flex flex-col items-center gap-6.5 md:order-2 md:flex-row"
          >
            <NuxtLink
              :to="localeRoute('/privacy')"
              class="capitalize text-[#A3A3A3] long-token"
              >{{ t('privacy-policy') }}</NuxtLink
            >
            <NuxtLink
              :to="localeRoute('/terms')"
              class="text-[#A3A3A3] long-token"
              >{{ t('eula') }}</NuxtLink
            >

            <UxLangSwitcher />
          </div>

          <div class="flex flex-row items-center gap-3.5 md:hidden">
            <NuxtLink
              v-for="item in externalLinks"
              class="flex"
              :to="item.to"
              target="_blank"
              :aria-label="item.ariaLabel"
            >
              <Icon v-if="item.icon" :name="item.icon" />
              <NuxtImg
                v-else
                :src="item.imgSrc"
                class="opacity-40 w-9 h-9"
                alt=""
              />
            </NuxtLink>
          </div>

          <div
            class="text-base md:text-xs lg:text-base text-center text-[#A3A3A3] md:order-0"
          >
            {{ t('copyright-belong') }} {{ date }}.
            {{ t('all-rights-reserved') }}
          </div>
        </div>
        <div
          v-if="$slots.title"
          class="text-sm text-[#A3A3A3] text-center mt-20"
        >
          {{ t('disclaimer') }}

          <slot name="title" />
        </div>
      </div>
    </div>
  </footer>
</template>
